import "@tennr/style/index.css";
import "./SPA.css";
import { useNavigate, useLocation } from "@tanstack/react-router";
import { SiteHeader } from "@tennr/frontend/components/SiteHeader";
import { WebGLBackground, type ColorStop } from "@tennr/frontend/components/WebGLBackground/WebGLBackground";
import { CSSBackground } from "@tennr/frontend/components/WebGLBackground/CSSBackground";
import { SmoothThresholdBackground } from "@tennr/frontend/components/WebGLBackground/SmoothThresholdBackground";
import { BackgroundColorProvider } from "@tennr/frontend/context";
import { ThemeColorSync } from "./ThemeColorSync";
import { Suspense, useEffect, useMemo, type FC, type ReactNode } from "react";

interface UniversalAppShellProps {
  children: ReactNode;
  colorStops?: ColorStop[];
  backgroundMode?: "webgl" | "css-gradient" | "css-threshold";
}

// define route-specific color stops
const routeColorStops = {
  home: [
    { color: [0.607, 0.306, 0.27], progress: 0.025 }, // #9B4E45
    { color: [0.607, 0.306, 0.27], progress: 0.15 }, // #9B4E45 (holding)
    // Transition zone
    { color: [0.77, 0.591, 0.545], progress: 0.2 }, // Interpolated buffer
    { color: [0.933, 0.875, 0.82], progress: 0.25 }, // #EEDFD1
    { color: [0.933, 0.875, 0.82], progress: 0.315 }, // #EEDFD1 (holding)
    { color: [0.933, 0.875, 0.82], progress: 0.85 }, // #EEDFD1 (holding)
    // Transition zone
    { color: [0.69, 0.604, 0.561], progress: 0.9 }, // Interpolated buffer
    { color: [0.447, 0.333, 0.302], progress: 0.95 }, // #72554D
    { color: [0.447, 0.333, 0.302], progress: 1.0 }, // #72554D
  ],

  privacy: [
    { color: [0.204, 0.275, 0.282], progress: 0.0 }, // #344648 - Blue start
    { color: [0.933, 0.875, 0.82], progress: 0.1 }, // #EEDFD1 - Beige middle
    { color: [0.153, 0.192, 0.153], progress: 0.95 }, //rgb(51, 73, 51) - Dark green end
  ],
  product: [
    { color: [0.372, 0.251, 0.216], progress: 0.025 }, // #5F4037 (dark brown hero)
    { color: [0.372, 0.251, 0.216], progress: 0.096 }, // #5F4037 (holding)
    // Transition zone
    { color: [0.65, 0.563, 0.518], progress: 0.14 }, // Interpolated buffer
    { color: [0.933, 0.875, 0.82], progress: 0.18 }, // #EEDFD1 (common gray)
    { color: [0.933, 0.875, 0.82], progress: 0.895 }, // #EEDFD1 (holding)
    // Transition zone
    { color: [0.808, 0.599, 0.553], progress: 0.92 }, // Interpolated buffer
    { color: [0.682, 0.322, 0.286], progress: 0.95 }, // #AE5249 (orange-ish red)
    { color: [0.682, 0.322, 0.286], progress: 1.0 }, // #AE5249 (holding)
  ],
  about: [
    { color: [0.15, 0.19, 0.15], progress: 0.0 }, // #273127 - Dark green start
    { color: [0.933, 0.875, 0.82], progress: 0.3 }, // #EEDFD1 - Beige middle
    { color: [0.372, 0.251, 0.216], progress: 0.9 }, // #5F4037 - Brown end
  ],
  book: [
    // start with brand red
    { color: [0.682, 0.322, 0.287], progress: 0.0 }, // #AE5249

    { color: [0.835, 0.643, 0.557], progress: 0.2 }, // Interpolated
    { color: [0.933, 0.875, 0.82], progress: 0.2 }, // #EEDFD1
    { color: [0.933, 0.875, 0.82], progress: 0.5 }, // #EEDFD1 (holding)
    { color: [0.48, 0.526, 0.481], progress: 0.8 }, // Interpolated
    // transition to dark green for footer
    { color: [0.254, 0.351, 0.293], progress: 0.9 }, // Interpolated
    { color: [0.15, 0.19, 0.15], progress: 0.95 }, // #273127
    { color: [0.15, 0.19, 0.15], progress: 1.0 }, // #273127 (holding)
  ],
  studies: [
    { color: [0.372, 0.251, 0.216], progress: 0.0 }, // #5F4037 (holding)

    { color: [0.372, 0.251, 0.216], progress: 0.1 }, // #5F4037

    // transition to light beige
    { color: [0.77, 0.538, 0.495], progress: 0.12 }, // Interpolated
    { color: [0.933, 0.875, 0.82], progress: 0.15 }, // #EEDFD1
    { color: [0.933, 0.875, 0.82], progress: 0.5 }, // #EEDFD1 (holding)
    { color: [0.933, 0.875, 0.82], progress: 0.7 }, // #EEDFD1 (holding)
    { color: [0.682, 0.322, 0.287], progress: 1.0 }, // #AE5249
  ],
  fax: [
    { color: [0.608, 0.306, 0.271], progress: 0.0 }, // #344648 - Blue start
    { color: [0.608, 0.306, 0.271], progress: 0.2 }, // #EEDFD1 - Beige middle
    { color: [0.153, 0.192, 0.153], progress: 0.95 }, //rgb(51, 73, 51) - Dark green end
  ],
  caseStudyDetail: [
    // start with light beige
    { color: [0.933, 0.875, 0.82], progress: 0.0 }, // #EEDFD1
    { color: [0.933, 0.875, 0.82], progress: 0.1 }, // #EEDFD1 (holding)
    { color: [0.933, 0.875, 0.82], progress: 0.7 }, // #EEDFD1 (long hold)
    // transition to dark green for footer
    { color: [0.48, 0.526, 0.48], progress: 0.85 }, // Interpolated
    { color: [0.15, 0.19, 0.15], progress: 0.95 }, // #273127
    { color: [0.15, 0.19, 0.15], progress: 1.0 }, // #273127 (holding)
  ],
  careers: [
    // start with brand red
    { color: [0.682, 0.322, 0.287], progress: 0.0 }, // #AE5249
    { color: [0.682, 0.322, 0.287], progress: 0.05 }, // #AE5249 (holding)
    { color: [0.835, 0.643, 0.557], progress: 0.07 }, // Interpolated
    { color: [0.933, 0.875, 0.82], progress: 0.1 }, // #EEDFD1
    { color: [0.933, 0.875, 0.82], progress: 0.5 }, // #EEDFD1 (holding)
    { color: [0.48, 0.526, 0.481], progress: 0.75 }, // Interpolated
    // transition to dark green for footer
    { color: [0.254, 0.351, 0.293], progress: 0.85 }, // Interpolated
    { color: [0.15, 0.19, 0.15], progress: 0.95 }, // #273127
    { color: [0.15, 0.19, 0.15], progress: 1.0 }, // #273127 (holding)
  ],
  performanceTest: [
    // High contrast colors for testing
    { color: [1, 1, 1], progress: 0.0 }, // Pure white
    { color: [0, 0, 0], progress: 0.2 }, // Pure black
    { color: [1, 0, 0], progress: 0.4 }, // Pure red
    { color: [0, 1, 0], progress: 0.6 }, // Pure green
    { color: [0, 0, 1], progress: 0.8 }, // Pure blue
    { color: [1, 1, 0], progress: 1.0 }, // Yellow
  ],
  events: [
    { color: [0.372, 0.251, 0.216], progress: 0.025 }, // #5F4037 (dark brown hero)
    { color: [0.372, 0.251, 0.216], progress: 0.096 }, // #5F4037 (holding)
    // Transition zone
    { color: [0.65, 0.563, 0.518], progress: 0.14 }, // Interpolated buffer
    { color: [0.933, 0.875, 0.82], progress: 0.18 }, // #EEDFD1 (common gray)
    { color: [0.933, 0.875, 0.82], progress: 0.895 }, // #EEDFD1 (holding)
    // Transition zone
    { color: [0.808, 0.599, 0.553], progress: 0.92 }, // Interpolated buffer
    { color: [0.682, 0.322, 0.286], progress: 0.95 }, // #AE5249 (orange-ish red)
    { color: [0.682, 0.322, 0.286], progress: 1.0 }, // #AE5249 (holding)
  ],
};

// Simplified color stops for threshold-based transitions (3 main colors per page)
const thresholdColorStops = {
  privacy: [
    { color: [0.204, 0.275, 0.282], progress: 0.0 }, // #344648 - Blue start
    { color: [0.933, 0.875, 0.82], progress: 0.1 }, // #EEDFD1 - Beige middle
    { color: [0.153, 0.192, 0.153], progress: 0.95 }, //rgb(51, 73, 51) - Dark green end
  ],
  fax: [
    { color: [0.608, 0.306, 0.271], progress: 0.0 }, // #344648 - Blue start
    { color: [0.608, 0.306, 0.271], progress: 0.2 }, // #EEDFD1 - Beige middle
    { color: [0.153, 0.192, 0.153], progress: 0.80 }, //rgb(51, 73, 51) - Dark green end
  ],
  home: [
    { color: [0.607, 0.306, 0.27], progress: 0.0 }, // #9B4E45 - Red start
    { color: [0.933, 0.875, 0.82], progress: 0.15 }, // #EEDFD1 - Beige middle
    { color: [0.372, 0.251, 0.216], progress: 0.9 }, // #5F4037 - Dark brown end
  ],
  product: [
    { color: [0.372, 0.251, 0.216], progress: 0.0 }, // #5F4037 - Dark brown start
    { color: [0.933, 0.875, 0.82], progress: 0.08 }, // #EEDFD1 - Beige middle
    { color: [0.607, 0.306, 0.27], progress: 0.92 }, // #9B4E45 - Red end
  ],
  about: [
    { color: [0.15, 0.19, 0.15], progress: 0.0 }, // #273127 - Dark green start
    { color: [0.933, 0.875, 0.82], progress: 0.27 }, // #EEDFD1 - Beige middle
    { color: [0.372, 0.251, 0.216], progress: 0.96 }, // #5F4037 - Brown end
  ],
  book: [
    { color: [0.682, 0.322, 0.287], progress: 0.0 }, // #AE5249 - Red start
    { color: [0.933, 0.875, 0.82], progress: 0.2 }, // #EEDFD1 - Beige middle
    { color: [0.15, 0.19, 0.15], progress: 0.75 }, // #273127 - Dark green end
  ],
  studies: [
    { color: [0.372, 0.251, 0.216], progress: 0.0 }, // #5F4037 - Brown start
    { color: [0.933, 0.875, 0.82], progress: 0.05 }, // #EEDFD1 - Beige middle
    { color: [0.682, 0.322, 0.287], progress: 0.8 }, // #AE5249 - Red end
  ],
  caseStudyDetail: [
    { color: [0.933, 0.875, 0.82], progress: 0.0 }, // #EEDFD1 - Beige start
    { color: [0.984, 0.984, 0.976], progress: 0.15 }, // #FBFBF9 - Hold off-white
    { color: [0.15, 0.19, 0.15], progress: 0.85 }, // #273127 - Dark green end
  ],
  careers: [
    { color: [0.682, 0.322, 0.287], progress: 0.0 }, // #AE5249 - Red start
    { color: [0.933, 0.875, 0.82], progress: 0.1 }, // #EEDFD1 - Beige middle
    { color: [0.15, 0.19, 0.15], progress: 0.95 }, // #273127 - Dark green end
  ],
  performanceTest: [
    { color: [1, 1, 1], progress: 0.0 }, // Pure white
    { color: [1, 0, 0], progress: 0.4 }, // Pure red
    { color: [0, 0, 1], progress: 0.8 }, // Pure blue
  ],
  press: [
    { color: [0.933, 0.875, 0.82], progress: 0.0 }, // #EEDFD1 - Beige start
    { color: [0.933, 0.875, 0.82], progress: 0.15 }, // #EEDFD1 - Beige middle
    { color: [0.153, 0.192, 0.153], progress: 0.9 }, // #273127 - Dark green end
  ],
  events: [
    { color: [0.372, 0.251, 0.216], progress: 0.0 }, // #5F4037 - Dark brown start
    { color: [0.933, 0.875, 0.82], progress: 0.08 }, // #EEDFD1 - Beige middle
    { color: [0.607, 0.306, 0.27], progress: 0.92 }, // #9B4E45 - Red end
  ],
};

/**
 * UniversalAppShell - The universal wrapper component for all pages
 * Provides consistent navigation, WebGL background, and smooth scrolling
 * without the scroll-rig dependency
 */
export const UniversalAppShell: FC<UniversalAppShellProps> = ({
  children,
  colorStops: providedColorStops,
  backgroundMode = "css-threshold", // Default to threshold mode for smooth transitions
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // determine which color stop config to use based on mode
  const useThresholdStops = backgroundMode === "css-threshold";
  const stopConfig = useThresholdStops ? thresholdColorStops : routeColorStops;

  // determine color stops based on current route
  const colorStops = useMemo(() => {
    if (providedColorStops) {
      return providedColorStops;
    }

    const pathname = location.pathname;
    if (pathname === "/" || pathname === "") {
      return stopConfig.home;
    } else if (pathname === "/product") {
      return stopConfig.product;
    } else if (pathname === "/about") {
      return stopConfig.about;
    } else if (pathname === "/privacy-policy") {
      return stopConfig.privacy;
    } else if (pathname === "/book-a-demo" || pathname.includes("/lp/")) {
      return stopConfig.book;
    } else if (pathname === "/careers") {
      return stopConfig.careers;
    } else if (pathname === "/customer-stories") {
      return stopConfig.studies;
    } else if (pathname.startsWith("/customer-stories/")) {
      return stopConfig.caseStudyDetail;
    } else if (pathname === "/performance-test") {
      return stopConfig.performanceTest;
    } else if (pathname === "/press") {
      return stopConfig.press;
    } else if (pathname === "/events") {
      return stopConfig.events
    } else if (pathname === "/fax-it") {
      return stopConfig.fax
    } else {
      return stopConfig.home;
    }
  }, [location.pathname, providedColorStops, stopConfig]);

  // reset scroll position when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // const handleNavigate = (page: string) => {
  //   if (page === "home") {
  //     navigate({ to: "/" });
  //   } else if (page === "product") {
  //     navigate({ to: "/product" });
  //   } else if (page === "about") {
  //     navigate({ to: "/about" });
  //   } else if (page === "book") {
  //     navigate({ to: "/book-a-demo" });
  //   }
  //   // scroll to top when changing pages
  //   window.scrollTo(0, 0);
  // };

  // Render the appropriate background based on mode
  const renderBackground = () => {
    switch (backgroundMode) {
      case "webgl":
        return (
          <Suspense fallback={<CSSBackground colorStops={colorStops} />}>
            <WebGLBackground colorStops={colorStops} />
          </Suspense>
        );
      case "css-gradient":
        return <CSSBackground colorStops={colorStops} />;
      case "css-threshold":
      default:
        return (
          <SmoothThresholdBackground colorStops={colorStops} transitionDuration={0.4} resetKey={location.pathname} />
        );
    }
  };

  return (
    <BackgroundColorProvider
      colorStops={colorStops}
      isTransitioning={backgroundMode === "css-threshold"}
      transitionDuration={0.4}
      defaultStrength={50} // Global strength for all dynamic headings
    >
      <ThemeColorSync />
      <div className="app">
        <SiteHeader />
        {renderBackground()}
        {children}
      </div>
    </BackgroundColorProvider>
  );
};
